import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks for booking operations
export const createBooking = createAsyncThunk(
  'bookings/create',
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await api.post('/bookings', bookingData);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getBookingDetails = createAsyncThunk(
  'bookings/getDetails',
  async (bookingToken, { rejectWithValue }) => {
    try {
      const response = await api.get(`/bookings/${bookingToken}`);

      if (response.success) {
        console.log("booking details", response.data)
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getUserBookings = createAsyncThunk(
  'bookings/getUserBookings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/bookings/my-bookings', { params });

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelBooking = createAsyncThunk(
  'bookings/cancel',
  async ({ bookingToken, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/bookings/cancel/${bookingToken}`, { reason });

      if (response.success) {
        return { bookingToken, ...response.data };
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePassengerDetails = createAsyncThunk(
  'bookings/updatePassenger',
  async ({ passengerId, passengerData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/bookings/passengers/${passengerId}`, passengerData);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  // Current booking process
  currentBooking: null,
  bookingStep: 1, // 1: flight selection, 2: passenger details, 3: payment, 4: confirmation

  // Booking creation
  createBookingLoading: false,
  createBookingError: null,

  // Booking details
  selectedBooking: null,
  bookingDetailsLoading: false,
  bookingDetailsError: null,

  // User bookings list
  userBookings: [],
  userBookingsLoading: false,
  userBookingsError: null,

  // Booking cancellation
  cancelBookingLoading: false,
  cancelBookingError: null,

  // Passenger details update
  updatePassengerLoading: false,
  updatePassengerError: null,

  // Passenger information
  passengers: [
    {
      id: 1,
      passenger_type: 'Adult',
      title: 'Mr',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      nationality: '',
      passport_number: '',
      passport_expiry: '',
      meal_preference: '',
      special_assistance: '',
    }
  ],

  // Selected flights for booking
  selectedOutboundFlight: null,
  selectedReturnFlight: null,

  // Booking preferences
  routeType: 'oneway', // oneway, roundtrip
  selectedSeats: [],
  paymentMethod: 'Card',

  // Booking summary
  totalAmount: 0,
  currency: 'USD',
};

// Booking slice
const bookingSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    // Booking process management
    setBookingStep: (state, action) => {
      state.bookingStep = action.payload;
    },

    nextBookingStep: (state) => {
      if (state.bookingStep < 4) {
        state.bookingStep += 1;
      }
    },

    previousBookingStep: (state) => {
      if (state.bookingStep > 1) {
        state.bookingStep -= 1;
      }
    },

    resetBookingProcess: (state) => {
      state.currentBooking = null;
      state.bookingStep = 1;
      state.selectedOutboundFlight = null;
      state.selectedReturnFlight = null;
      state.passengers = [initialState.passengers[0]];
      state.selectedSeats = [];
      state.totalAmount = 0;
      state.createBookingError = null;
    },

    // Flight selection
    setSelectedOutboundFlight: (state, action) => {
      state.selectedOutboundFlight = action.payload;
      state.totalAmount = action.payload.price_per_seat * state.passengers.length;
    },

    setSelectedReturnFlight: (state, action) => {
      state.selectedReturnFlight = action.payload;
      state.totalAmount = (state.selectedOutboundFlight?.price_per_seat || 0) * state.passengers.length +
        action.payload.price_per_seat * state.passengers.length;
    },

    setRouteType: (state, action) => {
      state.routeType = action.payload;
      if (action.payload === 'oneway') {
        state.selectedReturnFlight = null;
        state.totalAmount = (state.selectedOutboundFlight?.price_per_seat || 0) * state.passengers.length;
      }
    },

    // Passenger management
    addPassenger: (state) => {
      const newPassenger = {
        id: state.passengers.length + 1,
        passenger_type: 'Adult',
        title: 'Mr',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        nationality: '',
        passport_number: '',
        passport_expiry: '',
        meal_preference: '',
        special_assistance: '',
      };
      state.passengers.push(newPassenger);

      // Recalculate total amount
      const outboundPrice = state.selectedOutboundFlight?.price_per_seat || 0;
      const returnPrice = state.selectedReturnFlight?.price_per_seat || 0;
      state.totalAmount = (outboundPrice + returnPrice) * state.passengers.length;
    },

    removePassenger: (state, action) => {
      const passengerId = action.payload;
      state.passengers = state.passengers.filter(p => p.id !== passengerId);

      // Recalculate total amount
      const outboundPrice = state.selectedOutboundFlight?.price_per_seat || 0;
      const returnPrice = state.selectedReturnFlight?.price_per_seat || 0;
      state.totalAmount = (outboundPrice + returnPrice) * state.passengers.length;
    },

    updatePassenger: (state, action) => {
      const { passengerId, passengerData } = action.payload;
      const passengerIndex = state.passengers.findIndex(p => p.id === passengerId);
      if (passengerIndex !== -1) {
        state.passengers[passengerIndex] = { ...state.passengers[passengerIndex], ...passengerData };
      }
    },

    setPassengers: (state, action) => {
      state.passengers = action.payload;

      // Recalculate total amount
      const outboundPrice = state.selectedOutboundFlight?.price_per_seat || 0;
      const returnPrice = state.selectedReturnFlight?.price_per_seat || 0;
      state.totalAmount = (outboundPrice + returnPrice) * state.passengers.length;
    },

    // Seat selection
    setSelectedSeats: (state, action) => {
      state.selectedSeats = action.payload;
    },

    // Payment
    setPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },

    // Error clearing
    clearCreateBookingError: (state) => {
      state.createBookingError = null;
    },

    clearBookingDetailsError: (state) => {
      state.bookingDetailsError = null;
    },

    clearUserBookingsError: (state) => {
      state.userBookingsError = null;
    },

    clearCancelBookingError: (state) => {
      state.cancelBookingError = null;
    },

    clearUpdatePassengerError: (state) => {
      state.updatePassengerError = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Create Booking
      .addCase(createBooking.pending, (state) => {
        state.createBookingLoading = true;
        state.createBookingError = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.createBookingLoading = false;
        state.currentBooking = action.payload;
        state.bookingStep = 4; // Move to confirmation step
        state.createBookingError = null;
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.createBookingLoading = false;
        state.createBookingError = action.payload;
      })

      // Get Booking Details
      .addCase(getBookingDetails.pending, (state) => {
        state.bookingDetailsLoading = true;
        state.bookingDetailsError = null;
      })
      .addCase(getBookingDetails.fulfilled, (state, action) => {
        state.bookingDetailsLoading = false;
        state.selectedBooking = action.payload;
        state.bookingDetailsError = null;
      })
      .addCase(getBookingDetails.rejected, (state, action) => {
        state.bookingDetailsLoading = false;
        state.bookingDetailsError = action.payload;
        state.selectedBooking = null;
      })

      // Get User Bookings
      .addCase(getUserBookings.pending, (state) => {
        state.userBookingsLoading = true;
        state.userBookingsError = null;
      })
      .addCase(getUserBookings.fulfilled, (state, action) => {
        state.userBookingsLoading = false;
        state.userBookings = action.payload.bookings || [];
        state.userBookingsError = null;
      })
      .addCase(getUserBookings.rejected, (state, action) => {
        state.userBookingsLoading = false;
        state.userBookingsError = action.payload;
        state.userBookings = [];
      })

      // Cancel Booking
      .addCase(cancelBooking.pending, (state) => {
        state.cancelBookingLoading = true;
        state.cancelBookingError = null;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.cancelBookingLoading = false;
        state.cancelBookingError = null;

        // Update the booking in userBookings if it exists
        const bookingIndex = state.userBookings.findIndex(
          booking => booking.booking_token === action.payload.bookingToken
        );
        if (bookingIndex !== -1) {
          state.userBookings[bookingIndex].booking_status = 'Cancelled';
        }

        // Update selectedBooking if it's the same booking
        if (state.selectedBooking?.booking_token === action.payload.bookingToken) {
          state.selectedBooking.booking_status = 'Cancelled';
        }
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        state.cancelBookingLoading = false;
        state.cancelBookingError = action.payload;
      })

      // Update Passenger Details
      .addCase(updatePassengerDetails.pending, (state) => {
        state.updatePassengerLoading = true;
        state.updatePassengerError = null;
      })
      .addCase(updatePassengerDetails.fulfilled, (state, action) => {
        state.updatePassengerLoading = false;
        state.updatePassengerError = null;
        // Update passenger in selectedBooking if needed
        // This would require more complex logic based on your data structure
      })
      .addCase(updatePassengerDetails.rejected, (state, action) => {
        state.updatePassengerLoading = false;
        state.updatePassengerError = action.payload;
      });
  },
});

export const {
  setBookingStep,
  nextBookingStep,
  previousBookingStep,
  resetBookingProcess,
  setSelectedOutboundFlight,
  setSelectedReturnFlight,
  setRouteType,
  addPassenger,
  removePassenger,
  updatePassenger,
  setPassengers,
  setSelectedSeats,
  setPaymentMethod,
  clearCreateBookingError,
  clearBookingDetailsError,
  clearUserBookingsError,
  clearCancelBookingError,
  clearUpdatePassengerError,
} = bookingSlice.actions;

export default bookingSlice.reducer;